import { useEffect, useState } from "react";
import axios from "axios";

export const nexusApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/nexus`, withCredentials: true });

nexusApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("nexus_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const nexusToken = () => localStorage.getItem("nexus_token") || "";
export const fileUrl = (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/nexus/files/${fileId}?auth=${encodeURIComponent(nexusToken())}`;

export function nexusError(err, fallback = "Something went wrong. Please try again.") {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
}

export async function askStream(body, handlers) {
  const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/nexus/ask/stream`, {
    method: "POST", credentials: "include",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${nexusToken()}` },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) {
    let detail = "Nexus could not answer right now. Please try again.";
    try { detail = (await res.json()).detail || detail; } catch { /* keep default */ }
    throw new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done = false;
  while (!done) {
    const chunk = await reader.read();
    done = chunk.done;
    buf += decoder.decode(chunk.value || new Uint8Array(), { stream: !done });
    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const block = buf.slice(0, idx); buf = buf.slice(idx + 2);
      const ev = /^event: (.+)$/m.exec(block)?.[1];
      const data = /^data: (.+)$/m.exec(block)?.[1];
      if (!ev || !data) continue;
      const parsed = JSON.parse(data);
      if (ev === "error") throw new Error(parsed.detail);
      handlers[ev]?.(parsed);
    }
  }
}

export async function uploadInChunks(file, target, meta, onProgress) {
  const { data: init } = await nexusApi.post("/uploads/init", { filename: file.name, size: file.size, content_type: file.type || "application/octet-stream", target });
  const size = init.chunk_size;
  const total = Math.ceil(file.size / size);
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * size, Math.min(file.size, (i + 1) * size));
    await nexusApi.put(`/uploads/${init.upload_id}/chunk?index=${i}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
    onProgress?.(Math.round(((i + 1) / total) * 100));
  }
  const { data } = await nexusApi.post(`/uploads/${init.upload_id}/complete`, meta);
  return data;
}

export const EVENT_KINDS = [
  { value: "training", label: "Training", color: "#d4a84d" },
  { value: "meeting", label: "Meeting", color: "#7fb3c8" },
  { value: "seminar", label: "Seminar", color: "#9ad1a6" },
  { value: "social", label: "Social", color: "#e3a0c7" },
  { value: "deadline", label: "Deadline", color: "#ff8a80" },
  { value: "other", label: "Other", color: "#b8b2a4" },
  { value: "holiday", label: "Public holiday", color: "#e0c9a0" },
];
export const VIDEO_CATEGORIES = ["Training", "Recording", "Product knowledge", "Sales & scripts", "Compliance", "Culture", "Other"];
export const kindMeta = (k) => EVENT_KINDS.find((x) => x.value === k) || EVENT_KINDS[5];

export const fmtDate = (iso, opts = {}) => (iso ? new Date(iso.length <= 10 ? `${iso}T00:00` : iso).toLocaleDateString("en-SG", { weekday: "short", day: "numeric", month: "short", ...opts }) : "");
export const fmtTime = (iso) => (iso && iso.length > 10 ? new Date(iso).toLocaleTimeString("en-SG", { hour: "numeric", minute: "2-digit" }) : "");
export const fmtDuration = (s) => (!s ? "" : s >= 3600 ? `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m` : `${Math.max(1, Math.round(s / 60))} min`);

let holidaysCache = null;
export const useHolidays = () => {
  const [map, setMap] = useState(holidaysCache || {});
  useEffect(() => {
    if (holidaysCache) return;
    nexusApi.get("/holidays").then((r) => { holidaysCache = r.data; setMap(r.data); }).catch(() => {});
  }, []);
  return map;
};
export const holidayFor = (map, start, end) => {
  const s = (start || "").slice(0, 10), e = (end || start || "").slice(0, 10);
  if (!s) return null;
  const d = new Date(`${s}T00:00`);
  for (let i = 0; i <= 30; i++) {
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    if (iso > e) break;
    if (map[iso]) return { date: iso, name: map[iso] };
    d.setDate(d.getDate() + 1);
  }
  return null;
};

export const useNexusManifest = () => {
  useEffect(() => {
    const m = document.querySelector("link[rel=manifest]"); if (m) m.href = "/nexus-manifest.json?v=2";
    const a = document.querySelector("link[rel=apple-touch-icon]"); if (a) a.href = "/nexus-apple-touch-icon.png?v=2";
  }, []);
};
